

/**
 * Format an amount already in rupees (not paise) as an Indian-locale
 * currency string, e.g. `formatCurrency(123456.7)` -> `"₹1,23,457"`.
 *
 * Uses `Intl.NumberFormat` with the `en-IN` locale for correct Indian digit
 * grouping (lakh/crore) rather than a manual `toFixed` + string-splice, which
 * is how currency is formatted ad hoc elsewhere in the legacy codebase.
 *
 * @param {number | string | null | undefined} amountInRupees
 * @returns {string} e.g. "₹1,234" — "—" for null/undefined/non-numeric input.
 */
export function formatCurrency(amountInRupees, { fractionDigits = 0 } = {}) {
    const amount =
        typeof amountInRupees === "string" ? Number(amountInRupees) : amountInRupees;
    if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";

    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(amount);
}

/**
 * Join the non-empty parts of an address object into a single
 * comma-separated display string. Shape follows real usage in
 * `src/components/modal-forms/create-address/index.jsx` and
 * `src/components/address-print/OrderAddressPrintLabel.jsx`
 * (`{ line, landmark, district, state, pincode, country }`) rather than a
 * generic `{line1, line2, city}` guess.
 *
 * @param {{ line?: string, landmark?: string, district?: string, state?: string, pincode?: string, country?: string } | null | undefined} address
 * @returns {string} e.g. "12 MG Road, Near Metro, Gautam Buddh Nagar, Uttar Pradesh, 201301" — "" if address is missing/empty.
 */
export function formatAddress(address) {
    if (!address) return "";
    const { line, landmark, district, state, pincode, country } = address;
    return [line, landmark, district, state, pincode, country]
        .map((part) => (typeof part === "string" ? part.trim() : part))
        .filter((part) => part !== undefined && part !== null && part !== "")
        .join(", ");
}

/**
 * Format a total-minutes duration as a compact "Nd Nh" / "Nh Nm" / "Nm"
 * string. The day/hour/minute breakdown below is ported byte-for-byte from
 * `fmtMinutes` in `src/pages/glp1-dashboard/components/TATBar.jsx` (also
 * duplicated in `src/pages/glp1-dashboard/index.jsx`), so existing TAT
 * displays read identically if/when they migrate to this helper. The only
 * addition is the null/undefined guard below: `fmtMinutes` relied on its
 * caller (`TATBar`) to skip rendering for nullish minutes, but this is a
 * standalone formatter with no such wrapper, so it guards itself.
 *
 * @param {number | null | undefined} minutes
 * @returns {string} e.g. "2d 3h", "5h 12m", "40m" — "—" for null/undefined.
 */
export function formatDuration(minutes) {
    if (minutes === null || minutes === undefined) return "—";
    const d = Math.floor(minutes / 1440);
    const h = Math.floor((minutes % 1440) / 60);
    const m = minutes % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

/**
 * Format a date as "15 Oct 2026" — deliberately not "15/10/2026".
 *
 * `toLocaleDateString("en-IN")` (used across earlier v2 pages) doesn't zero-pad: a
 * column of dates renders "15/10/2026" next to "5/2/2026", each a different width, so
 * the column reads as ragged even though every value is correctly formatted. Spelling
 * the month out fixes the raggedness without needing to zero-pad the day, and reads
 * unambiguously regardless of whether the reader defaults to day-first or month-first.
 *
 * @param {string | number | Date | null | undefined} value
 * @returns {string} e.g. "15 Oct 2026" — "—" for null/undefined/invalid.
 */
export function formatShortDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
